import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { ChevronDown, Filter } from "lucide-react";
import Cookies from "js-cookie";
import { Notification } from "../../Notification";
import { FaBars } from "react-icons/fa";
import ThreeDotLoader from "../../Loader";
import DateFilter from "../../filter/DateFilter";
import OrderFilterPanel from "../../Common/OrderFilterPanel";
import PaginationFooter from "../../Common/PaginationFooter";

import {
  handleInvoice,
  handleLabel,
  handleBulkDownloadLabel,
  handleBulkDownloadInvoice,
  ExportExcel,
  handleClone,
  handleManifest,
  handleBulkClone,
} from "../../Common/orderActions";
import OrdersTable from "../../Common/OrdersTable";
import MobileOrderCard from "../../Common/MobileOrderCard";
import NotFound from "../../assets/nodatafound.png";

import QuickActionButtons from "../../Common/QuickActionButtons";
import SelectedCountBadge from "../../Common/SelectedCountBadge";
const AllOrders = ({ userId: initialUserId }) => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [refresh, setRefresh] = useState(false);
  const dropdownRefs = useRef([]);
  const toggleButtonRefs = useRef([]);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const desktopActionRef = useRef(null);
  const mobileActionRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalPages, setTotalPages] = useState(1);

  const [orderId, setOrderId] = useState("");
  const [awbNumber, setAwbNumber] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [dateRange, setDateRange] = useState([{ startDate: null, endDate: null, key: "selection" }]);
  const [pickupAddresses, setPickupAddresses] = useState([]);
  const [selectedPickupAddress, setSelectedPickupAddress] = useState("");
  const [courierOptions, setCourierOptions] = useState([]);
  const [selectedCourier, setSelectedCourier] = useState("");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  const [tableHeight, setTableHeight] = useState("calc(100vh - 260px)");
  const tableRef = useRef(null);
  const [dropdownDirection, setDropdownDirection] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedUserId, setSelectedUserId] = useState(initialUserId || new URLSearchParams(location.search).get("userId"));
  const [selectedStatus, setSelectedStatus] = useState("All");

  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const updateHeight = () => {
      if (tableRef.current) {
        const top = tableRef.current.getBoundingClientRect().top;
        const remainingHeight = window.innerHeight - top - 50;
        setTableHeight(`${remainingHeight}px`);
      }
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopActionRef.current && !desktopActionRef.current.contains(event.target)) {
        setDesktopDropdownOpen(false);
      }
      if (mobileActionRef.current && !mobileActionRef.current.contains(event.target)) {
        setMobileDropdownOpen(false);
      }

      const clickedAnyToggleButton = toggleButtonRefs.current.some(
        (ref) => ref && ref.contains(event.target)
      );
      const clickedInsideAnyDropdown = dropdownRefs.current.some(
        (ref) => ref && ref.contains(event.target)
      );

      if (!clickedInsideAnyDropdown && !clickedAnyToggleButton) {
        setDropdownOpen(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = Cookies.get("session");
      const params = {
        page,
        limit,
        status: selectedStatus === "All" ? undefined : selectedStatus,
        searchQuery: searchQuery || undefined,
        orderId: orderId || undefined,
        awbNumber: awbNumber || undefined,
        courier: selectedCourier || undefined,
        paymentType: paymentType || undefined,
        startDate: dateRange[0].startDate?.toISOString(),
        endDate: dateRange[0].endDate?.toISOString(),
        pickupContactName: selectedPickupAddress || undefined,
        userId: selectedUserId || undefined,
      };

      const response = await axios.get(`${REACT_APP_BACKEND_URL}/b2b/getb2badminorder`, {
        params,
        headers: { authorization: `Bearer ${token}` },
      });

      setOrders(response.data.orders);
      setTotalPages(response.data.totalPages);
      if (response.data.couriers) setCourierOptions(response.data.couriers);
      if (response.data.pickupLocations) setPickupAddresses(response.data.pickupLocations);

    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, limit, refresh, dateRange, selectedUserId, selectedStatus]);

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const handleUpdateOrder = (order) => {
    const orderUserId = order.userId?._id || order.userId;
    const url = `/dashboard/order/neworder?updateId=${order._id}${orderUserId ? `&userId=${orderUserId}` : ''}`;
    navigate(url);
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === orders.length) setSelectedOrders([]);
    else setSelectedOrders(orders.map(o => o._id));
  };

  const handleCheckboxChange = (orderId) => {
    setSelectedOrders(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setOrderId("");
    setAwbNumber("");
    setPaymentType("");
    setSelectedPickupAddress("");
    setSelectedCourier("");
    setSelectedStatus("All");
    setDateRange([{ startDate: null, endDate: null, key: "selection" }]);
    setPage(1);
    setRefresh(prev => !prev);
  };

  const quickActions = [
    { label: "Export", onClick: () => ExportExcel({ selectedOrders, orders }) },
  ];

  return (
    <div className="w-full">
      <div className="flex w-full sm:mb-2 md:flex-row flex-col sm:mt-2 justify-between items-center gap-1">
        <div className="flex flex-row items-center gap-2 w-full md:w-auto">
          <div className="flex-1 md:w-[200px]">
            <DateFilter onDateChange={setDateRange} clearTrigger={refresh} />
          </div>
          <button
            onClick={() => setIsFilterPanelOpen(true)}
            className="flex-shrink-0 flex items-center justify-center gap-2 px-3 py-2 bg-white border rounded-lg sm:text-[12px] text-[10px] font-[600] text-gray-500 hover:bg-gray-50 transition-all shadow-sm whitespace-nowrap min-w-[120px]"
          >
            <Filter className="w-4 h-4 text-brand-primary" />
            More Filters
          </button>
          <div className="hidden md:block">
            {(searchQuery || orderId || awbNumber || paymentType || selectedPickupAddress || selectedCourier || selectedUserId || selectedStatus !== "All") && (
              <button onClick={handleClearFilters} className="text-[12px] text-red-500 hover:underline font-[600] whitespace-nowrap">
                Clear All Filters
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 w-auto justify-end">
          <SelectedCountBadge count={selectedOrders.length} className="hidden md:inline-flex items-center bg-gray-100 px-2 py-1.5 rounded-lg" />
          <QuickActionButtons selectedCount={selectedOrders.length} className="hidden md:flex" actions={quickActions} />
          <div className="hidden md:block relative" ref={desktopActionRef}>
            <button
              disabled={selectedOrders.length === 0}
              className={`py-2 px-3 rounded-lg text-[12px] font-[600] flex items-center gap-2 transition-all border ${selectedOrders.length === 0
                ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                : "border-brand-primary text-brand-primary bg-white hover:bg-brand-primary/8 shadow-sm"
                }`}
              onClick={() => setDesktopDropdownOpen(!desktopDropdownOpen)}
            >
              Actions <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${desktopDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {desktopDropdownOpen && (
              <div className="absolute right-0 mt-1 w-48 text-[10px] bg-white border border-gray-200 shadow-sm z-[60] font-[600] overflow-hidden animate-popup-in">
                <ul className="py-1">
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer"
                    onClick={() => { ExportExcel({ selectedOrders, orders }); setDesktopDropdownOpen(false); }}>
                    Export Excel
                  </li>
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer"
                    onClick={() => { handleBulkClone(selectedOrders, setRefresh); setDesktopDropdownOpen(false); }}>
                    Bulk Clone
                  </li>
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer"
                    onClick={() => { handleBulkDownloadInvoice({ selectedOrders }); setDesktopDropdownOpen(false); }}>
                    Download Invoices
                  </li>
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer"
                    onClick={() => { handleBulkDownloadLabel({ selectedOrders }); setDesktopDropdownOpen(false); }}>
                    Download Labels
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div ref={tableRef} className="hidden md:block">
        <div style={{ height: tableHeight }} className="overflow-auto relative bg-white">
          <OrdersTable
            orders={orders}
            loading={loading}
            selectedOrders={selectedOrders}
            handleSelectAll={handleSelectAll}
            handleCheckboxChange={handleCheckboxChange}
            navigate={navigate}
            copiedOrderId={copiedOrderId}
            setCopiedOrderId={setCopiedOrderId}
            dropdownOpen={dropdownOpen}
            toggleDropdown={toggleDropdown}
            dropdownRefs={dropdownRefs}
            toggleButtonRefs={toggleButtonRefs}
            dropdownDirection={dropdownDirection}
            handleInvoice={handleInvoice}
            handleLabel={handleLabel}
            handleManifest={handleManifest}
            handleClone={handleClone}
            refresh={refresh}
            setRefresh={setRefresh}
            showShippingDetails={true}
            showUserDetails={true}
            handleUpdateOrder={handleUpdateOrder}
            isB2B={true}
          />
        </div>
        <PaginationFooter page={page} totalPages={totalPages} setPage={setPage} limit={limit} setLimit={setLimit} />
      </div>

      {/* Mobile View */}
      <div className="md:hidden w-full">
        <div className="flex items-center justify-between gap-2 mb-2 bg-white p-2 rounded-lg shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border flex-1">
            <input type="checkbox" checked={selectedOrders.length === orders.length && orders.length > 0} onChange={handleSelectAll} className="cursor-pointer accent-brand-primary w-3 h-3" />
            <span className="text-[10px] font-[600]">Select All</span>
            <SelectedCountBadge count={selectedOrders.length} />
          </div>

          <QuickActionButtons selectedCount={selectedOrders.length} actions={quickActions} />
          <div className="relative" ref={mobileActionRef}>
            <button
              disabled={selectedOrders.length === 0}
              className={`h-7 px-3 rounded-lg text-[12px] font-[600] flex items-center gap-1 transition-all border ${selectedOrders.length === 0
                ? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
                : "border-brand-primary text-brand-primary bg-white shadow-sm"
                }`}
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
            >
              <FaBars className={selectedOrders.length === 0 ? "text-gray-400" : "text-brand-primary"} />
              <span className="hidden sm:inline">Actions▼</span>
            </button>
            {mobileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-sm z-[60] text-[10px] font-[600] overflow-hidden animate-popup-in">
                <ul className="py-1">
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer" onClick={() => { ExportExcel({ selectedOrders, orders }); setMobileDropdownOpen(false); }}>Export Excel</li>
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer" onClick={() => { handleBulkClone(selectedOrders, setRefresh); setMobileDropdownOpen(false); }}>Bulk Clone</li>
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer" onClick={() => { handleBulkDownloadInvoice({ selectedOrders }); setMobileDropdownOpen(false); }}>Download Invoices</li>
                  <li className="px-3 py-2 text-gray-700 hover:bg-brand-primary/8 cursor-pointer" onClick={() => { handleBulkDownloadLabel({ selectedOrders }); setMobileDropdownOpen(false); }}>Download Labels</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div style={{ height: tableHeight }} className="overflow-y-auto space-y-2">
          {loading ? (
            <div className="flex justify-center py-6"><ThreeDotLoader /></div>
          ) : orders.length > 0 ? (
            orders.map((order, index) => (
              <MobileOrderCard
                key={order._id}
                order={order}
                index={index}
                selectedOrders={selectedOrders}
                handleCheckboxChange={handleCheckboxChange}
                toggleDropdown={toggleDropdown}
                dropdownOpen={dropdownOpen}
                dropdownRefs={dropdownRefs}
                toggleButtonRefs={toggleButtonRefs}
                dropdownDirection={dropdownDirection}
                handleInvoice={handleInvoice}
                handleLabel={handleLabel}
                handleManifest={handleManifest}
                handleClone={handleClone}
                refresh={refresh}
                setRefresh={setRefresh}
                navigate={navigate}
                showShippingDetails={true}
                showUserDetails={true}
                handleUpdateOrder={handleUpdateOrder}
                isB2B={true}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-6">
              <img src={NotFound} alt="No Data Found" className="w-60 h-60 object-contain mb-2" />
            </div>
          )}
        </div>
        <PaginationFooter page={page} totalPages={totalPages} setPage={setPage} limit={limit} setLimit={setLimit} />
      </div>

      <OrderFilterPanel
        isOpen={isFilterPanelOpen}
        onClose={() => setIsFilterPanelOpen(false)}
        searchQuery={searchQuery}
        orderId={orderId}
        awbNumber={awbNumber}
        paymentType={paymentType}
        pickupAddresses={pickupAddresses}
        selectedPickupAddress={selectedPickupAddress}
        selectedCourier={selectedCourier}
        selectedUserId={selectedUserId}
        selectedStatus={selectedStatus}
        onClearFilters={handleClearFilters}
        onApplyFilters={(filters) => {
          setSearchQuery(filters.searchQuery);
          setOrderId(filters.orderId);
          setAwbNumber(filters.awbNumber);
          setPaymentType(filters.paymentType);
          setSelectedPickupAddress(filters.selectedPickupAddress);
          setSelectedCourier(filters.selectedCourier);
          setSelectedStatus(filters.selectedStatus || "All");
          setPage(1);
          setRefresh(prev => !prev);
          setIsFilterPanelOpen(false);
        }}
        courierOptions={courierOptions}
        showAwb={true}
        showCourier={true}
        showUserSearch={true}
        showStatus={true}
      />
    </div>
  );
};

export default AllOrders;
